/**
 * Created by Jie Gao on 2019-06-07.
 */

import {ShakingDisplayObject} from "@nolimitcity/slot-game/bin/core/displayobject/ShakingDisplayObject";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {ReelEvent} from "@nolimitcity/slot-game/bin/core/reel/event/ReelEvent";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ReelStopPresentationController} from "@nolimitcity/slot-game/bin/core/reelstoppresentation/ReelStopPresentationController";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {ScreenEvent} from "@nolimitcity/slot-game/bin/core/screen/event/ScreenEvent";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {GGParsedGameData} from "../server/data/GGParsedGameData";
import {IReelStoppedData} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {TimelineLite} from "gsap";
import {GameMode} from "@nolimitcity/slot-game/bin/core/gamemode/GameMode";
import {GGGameConfig} from "../GGGameConfig";
import {GGParsedInitData} from "../server/data/GGParsedInitData";
import {GGSoundConfig} from "../GGSoundConfig";
import {GGGameModuleConfig} from "../GGGameModuleConfig";
import {GGReelView} from "../reel/GGReelView";
import {GGGameMode} from "../gamemode/GGGameMode";

export class ScatterAttention extends ReelStopPresentationController {
    private _mode: string;
    private _nextMode: string;
    private _reelsAfterCollapse: string[][];
    private _reelsAfterCollapseOriginal: string[][];
    private _allWinPositions: boolean[][];
    private _allScatters: StateReelSymbol[] = [];
    private _activeReelsNum: number;
    private _reelSizeNextSpin: number;
    private _shakingObjs: ShakingDisplayObject[] = [];
    private _timelines: TimelineLite[] = [];
    private _isFeatureBuy: boolean = false;
    private _anticipationPlaying: boolean = false;
    private _allReelsStopped: boolean = false;

    public get allScatters(): StateReelSymbol[] {
        return this._allScatters;
    }

    constructor(index: number) {
        super(index, "ScatterAttention");

        GGGameModuleConfig.scatterAttention = this;
    }

    public onReelStopSymbolAttention(data: IReelStoppedData): void {
        if (this._mode.indexOf("FREESPIN") > -1) {
            return;
        }

        const symbol: StateReelSymbol = data.symbols![0];
        if (symbol.symName != ReelSymbolName.SCATTER) {
            return;
        }

        // ignore if old scatter drop in avalanche.
        if (this._allScatters.indexOf(symbol) > -1) {
            return;
        }

        // Move to top
        // Normal mode
        this._allScatters.push(symbol);

        GGGameModuleConfig.soundPlayer.playSoundEffect((<any>GGSoundConfig)[`SCATTER_${Math.min(4, this._allScatters.length)}`]);

        const reelsLeft: number = this._activeReelsNum - (data.reelId + 1);

        if (this._allScatters.length + reelsLeft < 3) {
            return; // No chance to win.
        }

        switch (this._allScatters.length) {
            case 1:
                if (!data.quickStop) {
                    // play attention
                    symbol.changeState({
                        state: GGGameConfig.instance.SYMBOL_STATES.landing
                    });
                }
                break;
            case 2:
                if (!data.quickStop) {
                    // both shake attention
                    this._allScatters.forEach((scatter: StateReelSymbol) => {
                        const shakeObj: ShakingDisplayObject = new ShakingDisplayObject(scatter);
                        shakeObj.shake(0.1, 2);
                        this._shakingObjs.push(shakeObj);

                        symbol.changeState({
                            state: GGGameConfig.instance.SYMBOL_STATES.attention
                        });
                    });
                    this.playAnticipation();
                }
                break;
            default:
                // all play win to frame 19 and stay.
                this.stopShake();
                const timelineWin: TimelineLite = new TimelineLite({onComplete: () => this.onAnimationComplete()});
                this._allScatters.forEach((scatter: StateReelSymbol) => {
                    (<GGReelView>Reels.getReel(data.reelId)!.view).addSymbolToWinContainer(symbol, true);
                    timelineWin.add(scatter.changeState({
                        state: GGGameConfig.instance.SYMBOL_STATES.plus
                    }), 0);
                });

                // #391
                if (this._allScatters.length === 4) {
                    this.stopAnticipation(false);
                }

                this._timelines.push(timelineWin);

                break;
        }
    }

    public onReelSpinStopped(data: IReelStoppedData): void {
        if (data.reelId === 5) {
            // block it from finished and wait for all complete.
            return;
        }

        this.dispatchCompleteEvent(data.reelId);
    }

    public onAllReelsSpinAnimationStopped(): void {
        this._allReelsStopped = true;
        this.stopShake();
        this.onAnimationComplete();
        if (this._nextMode.indexOf("AVALANCHE") > -1 || GGGameConfig.instance.FAULTY_WIN_CAP) {
            return;
        }
        this.stopAnticipation(this._allScatters.length < 3);
    }

    protected addFeatureEventHandlers(): void {
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ScreenEvent.GAME_RESTORE, (event: GameEvent) => this.onRestore());
        EventHandler.addEventListener(this, SpinEvent.BEFORE_START, (event: GameEvent) => this.onBeforeSpinStart());
        EventHandler.addEventListener(this, ReelEvent.ALL_REELS_STOP_SPIN, (event: GameEvent) => this.onStopSpin((event.params[0])));
        EventHandler.addEventListener(this, ReelEvent.ALL_REELS_SPIN_ANIMATION_STOPPED, (event: GameEvent) => this.onAllReelsSpinAnimationStopped());
    }

    protected parseFeatureGameData(data: GGParsedGameData): void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;
        this._isFeatureBuy = (data.wasFeatureBuy && (data.doubleBombSpins<0));

        this._allWinPositions = data.allWinPositions;
        this._reelSizeNextSpin = data.gameAreaSizeNextSpin;
        this._reelsAfterCollapseOriginal = data.reelsAfterCollapse;
        this._reelsAfterCollapse = this.parseReelsAfterCollapse(data.reelsAfterCollapse);
    }

    private onStopSpin(stopData: { quickStop: boolean; fastSpin: boolean }): void {
        if (stopData.quickStop) {
            this.stopShake();
        }
    }

    private onInitDataParsed(data: GGParsedInitData): void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;
        this._reelSizeNextSpin = data.gameAreaSize;
        this._allWinPositions = data.allWinPositions;
        this._reelsAfterCollapse = data.reelsAfterCollapse;
        this._reelsAfterCollapseOriginal = data.reelsAfterCollapse;
    }

    private parseReelsAfterCollapse(data: string[][]): string[][] {
        const result: string[][] = [];
        data.forEach((reelAfterCollapse: string[], reelId: number) => {
            if (this._allWinPositions[reelId].includes(true)) {
                const reelResult: string[] = [];
                reelAfterCollapse.forEach((symbol: string) => {
                    if (symbol != "X") {
                        reelResult.push(symbol);
                    }
                });
                result.push(reelResult);
            } else {
                result.push(reelAfterCollapse.concat());
            }
        });
        return result;
    }

    private onRestore(): void {
        // #562
        if (this._nextMode === GameMode.NORMAL_AVALANCHE) {
            this._allScatters = [];
            this._reelsAfterCollapse.forEach((reel: string[], reelId: number) => {
                reel.forEach((symbolName: string, symId: number) => {
                    if (symbolName === ReelSymbolName.SCATTER) { // No need to think about winPositions here?
                        const symbol: StateReelSymbol = Reels.getSymbol(reelId, symId)!;
                        if (symbol == null || symbol.symName != ReelSymbolName.SCATTER) {
                            debugger;
                        }
                        this._allScatters.push(symbol);
                    }
                });
            });

            if (this._allScatters.length >= 3) {
                this._allScatters.forEach((scatter: StateReelSymbol) => {
                    scatter.changeState({
                        state: GGGameConfig.instance.SYMBOL_STATES.plus
                    });
                });
                this.playAnticipation();
            }
        }
    }

    private onBeforeSpinStart(): void {
        this._allReelsStopped = false;
        if (this._nextMode.indexOf("FREESPIN") > -1 || GGGameConfig.instance.FAULTY_WIN_CAP) {
            return;
        }

        this._activeReelsNum = GGGameConfig.instance.REELS_NUM;

        if (this._nextMode === GameMode.NORMAL || this._nextMode === GGGameMode.DOUBLE_BOMB) {
            this._allScatters = [];
            return;
        }

        // normal avalanche
        let activeReelCounter: number = 0;
        this._reelsAfterCollapseOriginal.forEach((reel: string[]) => {
            //Restore state
            if (reel[8-this._reelSizeNextSpin] === "X") {
                activeReelCounter++;
            }
        });
        this._activeReelsNum = activeReelCounter;

        if (this._allScatters.length === 2) {
            this._allScatters.forEach((scatter: StateReelSymbol) => {
                scatter.changeState({
                    state: GGGameConfig.instance.SYMBOL_STATES.attention,
                    replay: true,
                    onCompleteCallback: () => {
                        const shakeObj: ShakingDisplayObject = new ShakingDisplayObject(scatter.currentStateAnimation!);
                        shakeObj.shake(0.1, 2);
                        this._shakingObjs.push(shakeObj);
                    }
                });
            });
        }

        // #391
        if (this._allScatters.length > 1 && this._allScatters.length < 4) {
            this.playAnticipation();
        }
    }

    private playAnticipation(): void {
        if (this._anticipationPlaying || this._isFeatureBuy) {
            return;
        }
        GGGameModuleConfig.soundPlayer.playAnticipationSound();
        GGGameModuleConfig.background.playNearWin();
        this._anticipationPlaying = true;
    }

    private stopAnticipation(fail: boolean): void {
        if (!this._anticipationPlaying) {
            return;
        }
        GGGameModuleConfig.background.stopNearWin();
        GGGameModuleConfig.soundPlayer.stopAnticipationSound();
        this._anticipationPlaying = false;
        GGGameModuleConfig.soundPlayer.playSoundEffect(fail ? GGSoundConfig.ANTICIPATION_FAIL : GGSoundConfig.ANTICIPATION_END);
    }

    private onAnimationComplete(): void {
        if (!this._allReelsStopped) {
            return;
        }
        if (this._timelines.some((timeline: TimelineLite): boolean => {
            return timeline.isActive();
        })) {
            return;
        }

        this._timelines = [];
        this.dispatchCompleteEvent(5);
    }

    private stopShake(): void {
        this._shakingObjs.forEach((obj: ShakingDisplayObject) => {
            if (obj) {
                obj.stopShake();
            }
        });
        this._shakingObjs = [];
    }
}